/**
 * Calls to the account API that Better Auth's client does not cover.
 *
 * Our own plugin endpoints — agreements, subscriptions — are plain REST rather
 * than plugin actions, so they are fetched directly.
 *
 * `credentials: 'include'` on EVERY call. This is a cross-origin app: the
 * session cookie is scoped to the parent domain and the browser will not attach
 * it otherwise, which presents as "signed out" on a page that plainly is not.
 */
import { authClient, LOGIN_URL } from './auth-client.js';
import type { PortalSubscription } from './subscriptions.js';
import type { PortalConnection } from './connections.js';
import type { TesterCardResponse } from './testers.js';
import type {
  DependentAgreement,
  DependentView,
  FamilyRole,
  InvitationPreview,
  MyFamily,
} from './family.js';

export type { PortalSubscription, PortalConnection };

const BASE = import.meta.env.VITE_AUTH_URL ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * The service's own `code`, when it sent one.
     *
     * The body has carried this all along — the avatar note below quotes a
     * response containing both — and only `message` was being kept. A caller
     * that needs to BRANCH wants the code: the human sentence is written for
     * a person and may be reworded at any time, whereas the code is the part
     * the service treats as an interface.
     *
     * Optional because not every refusal has one, and a caller must be able to
     * tell "no code was sent" from a code it does not recognise.
     */
    readonly code?: string,
    /**
     * The whole parsed body, for a refusal that carries more than a sentence.
     *
     * `EMAIL_MISMATCH` (RidgeWill/auth#587) says whether the one-time address
     * correction is still available, and the screen offers it or not on that
     * alone. Unknown shape on purpose: only the caller knows which refusal it is
     * reading, and it checks the field rather than trusting a cast.
     */
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}/api/auth${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    /**
     * ── THE SERVER ALREADY WROTE THE SENTENCE. USE IT. ────────────────
     *
     * This threw `POST /avatar failed: 400` and discarded the body, so somebody
     * who picked an SVG was shown a status code — while the service was
     * answering, in the same response:
     *
     *   { "message": "SVG images cannot be used as avatars. Please upload a
     *     PNG, JPEG or WebP instead.", "code": "SVG_NOT_ALLOWED" }
     *
     * Same for TOO_LARGE, which names the 1 MB limit. Those messages exist
     * because the refusals are deliberate and a person needs to know what to do
     * differently; throwing them away turned a designed explanation into
     * something that reads like a bug in the portal.
     *
     * The status stays on the error for code to branch on — a 404 in particular
     * is worth naming separately, because the account deployment serves an
     * ALLOW-LIST and a path it does not serve returns 404 rather than an error
     * the endpoint produced. If a screen starts 404ing after a plugin upgrade,
     * that allow-list is where to look. What changes is only what a HUMAN sees.
     *
     * The fallback keeps the old string, because a body that is empty, HTML, or
     * not JSON must not turn a failed request into a failed error handler.
     */
    const fallback = `${init.method ?? 'GET'} ${path} failed: ${res.status}`;
    let message = fallback;
    let code: string | undefined;
    let body: unknown;
    try {
      body = await res.json();
      const fromServer = (body as { message?: unknown } | null)?.message;
      if (typeof fromServer === 'string' && fromServer.trim()) message = fromServer;
      const codeFromServer = (body as { code?: unknown } | null)?.code;
      if (typeof codeFromServer === 'string' && codeFromServer.trim()) code = codeFromServer;
    } catch {
      // Not JSON. Keep the fallback.
    }
    throw new ApiError(res.status, message, code, body);
  }
  return (await res.json()) as T;
}

/**
 * One agreement resolved to the version in force, as auth's `toResolved` sends
 * it (services/auth/src/lib/agreements.ts).
 *
 * ── `agreementSlug`, NOT `slug` ──────────────────────────────────────
 *
 * This type said `slug` and `version`-to-accept for as long as the Agreements
 * screen existed, which made its Accept button send `{ slug, version }` to an
 * endpoint that takes `{ agreementVersionId }` and answers anything else with a
 * 400. Nothing had ever been published, so nothing was ever pending, so nobody
 * pressed it. The fields below are the server's.
 */
export interface Agreement {
  agreementSlug: string;
  name: string;
  /** null when the agreement is not one app's own; otherwise the app it belongs to. */
  appSlug: string | null;
  /**
   * The security group that alone owes this, e.g. `testers` for the Ridgewill
   * Testing Terms (auth#612). Org-wide is BOTH this and `appSlug` null. Absent
   * from an auth that predates it, which reads the same as null.
   */
  groupSlug?: string | null;
  version: string;
  /** What `/agreements/accept` takes. Must be the version in force. */
  agreementVersionId: string;
  /** The exact text being agreed to, stored inline and immutable. */
  content: string;
  contentFormat: 'markdown' | 'html' | string;
  documentUrl: string | null;
  /** "What changed", written by whoever published the version. May be empty. */
  summary: string;
  requiresReacceptance: boolean;
  /** Only on `/agreements/current`. */
  accepted?: boolean;
  acceptedAt?: string | null;
  /**
   * For someone who needs a guardian's half too (auth#615): which halves exist.
   * `{ self: true, guardian: false }` means this person has accepted and is
   * waiting for their guardian, which is not something they can do here.
   */
  halves?: { self: boolean; guardian: boolean };
}

/**
 * Whether an owed agreement keeps the rest of the account closed: Ridgewill's
 * own, owed by everyone (the Terms and the Privacy Policy).
 *
 * NOT an app's own terms, which that app asks for, and NOT an agreement owed by
 * one group (auth#612). The Testing Terms govern Test Versions inside apps, and
 * this portal is not one: a tester must still be able to change a password,
 * contact support or close the account without agreeing to them first. The app
 * that ships the Test Version asks at entry, and the Agreements screen lists
 * them meanwhile.
 *
 * `!groupSlug` rather than `=== null`, so an auth from before the field still
 * gates on everything org-wide, as it did.
 */
export const gatesAccount = (a: Agreement): boolean => a.appSlug === null && !a.groupSlug;

/**
 * What this account still owes.
 *
 * THE AUTHORITATIVE ANSWER. `user.pendingAgreements` on the session is a cache
 * that cannot filter retired or out-of-scope rows, and the auth service says so
 * itself: the field is a hint, this endpoint is the answer.
 */
export const getPendingAgreements = () => call<{ pending: Agreement[] }>('/agreements/pending');

/**
 * Everything currently in force for this account, accepted or not.
 *
 * THE KEY IS `current`, and this said `agreements` for as long as the screen
 * existed. The endpoint answers `{ current: [...] }` — see the auth service's
 * `plugins/agreements.ts`, `ctx.json({ current })`.
 *
 * Nothing caught it. `call<T>` casts `res.json()` to whatever the call site
 * claims, so the compiler was told the shape rather than shown it, and a
 * successful 200 handed the screen `undefined`. The screen then treated a
 * missing array as "still loading" and span forever, with no console error and
 * no failed request. See Agreements.tsx for the second half of that.
 */
export const getCurrentAgreements = () => call<{ current: Agreement[] }>('/agreements/current');

export const acceptAgreement = (agreementVersionId: string) =>
  call<{ accepted: boolean }>('/agreements/accept', {
    method: 'POST',
    body: JSON.stringify({ agreementVersionId }),
  });

/**
 * Everything this person pays Ridgewill for, across every app (auth#435).
 *
 * ── NOT `/me/subscription`, AND THE DIFFERENCE IS THE CALLER ────────
 *
 * That endpoint is scoped by the token's `azp` so a product can ask only about
 * itself, and it answers for one app. This one is the person asking about
 * themselves from a session, and crossing applications is the entire point: it
 * is the product commitment that justified moving subscription ownership into
 * auth in the first place (adr/0016). A portal that could only see one app
 * would leave that ADR standing on nothing.
 *
 * Served by auth's account PLUGIN rather than its `/me` router, because the
 * portal holds a session cookie and no bearer token at all. Same split as
 * agreements above, for the same reason.
 *
 * ── ROWS ONLY, so an empty list is a real answer ────────────────────
 *
 * It does not list every registered application with `FREE` against the ones
 * somebody has never opened. So `[]` means "you pay us for nothing", which the
 * screen has to say out loud rather than render as an empty box.
 *
 * `subscriptions ?? []` is the caller's job, not this file's, and the reason is
 * `getCurrentAgreements` above: `call<T>` casts the body to whatever the call
 * site claims, so a key that does not exist is a type the compiler believes and
 * an `undefined` at runtime.
 */
export const getSubscriptions = () =>
  call<{ subscriptions: PortalSubscription[] }>('/account/subscriptions');

/**
 * The signed-in person's tester card (platform#56), served to the session by
 * auth's tester-cards plugin (RidgeWill/auth#645).
 *
 * `card` is null for most accounts, and that is a real answer: nobody has a
 * card until they make one on ridgewill.com. `apps` and `blockers` come back
 * either way, the same as the site reads them, so the summary resolves app
 * names and "still needed" from the server rather than from a copy here.
 *
 * Only the read and the delete are called from the portal. Building and
 * changing the card is the site's job; see lib/testers.
 */
export const getTesterCard = () => call<TesterCardResponse>('/account/tester-card');

/**
 * Deletes the card AND takes the person out of every tester security group,
 * in one call. `leftGroups` names the groups they were in.
 */
export const deleteTesterCard = () =>
  call<{ deleted: boolean; leftGroups: string[] }>('/account/tester-card', { method: 'DELETE' });

/**
 * Connections and blocks (RidgeWill/account#79), served to the session by
 * auth's account-connections plugin (RidgeWill/auth#486).
 *
 * The user id in a block or unblock is always the OTHER person; who is acting is
 * the session, and there is no parameter that could say otherwise. A row blocked
 * BY the other person is never sent here, so nothing on this screen can reveal
 * one.
 */
export const getConnections = () =>
  call<{ connections: PortalConnection[] }>('/account/connections');

/** Mutual, and not protection: see Connections.tsx. Also withdraws an invitation. */
export const dropConnection = (connectionId: string) =>
  call<{ dropped: boolean }>(`/account/connections/${encodeURIComponent(connectionId)}/drop`, {
    method: 'POST',
  });

export const getBlocks = () => call<{ blocks: PortalConnection[] }>('/account/blocks');

export const blockUser = (userId: string) =>
  call<{ block: PortalConnection }>('/account/blocks', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });

export const unblockUser = (userId: string) =>
  call<{ unblocked: boolean }>(`/account/blocks/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });

/**
 * Who can invite this account, in every Ridgewill app (account#91, over
 * RidgeWill/auth#505). auth reads it in `mayContact`, and a refusal it causes
 * looks exactly like a block to the person refused.
 */
export type WhoCanInvite = 'anyone' | 'connections';

export const getWhoCanInvite = () =>
  call<{ whoCanInvite: WhoCanInvite }>('/account/who-can-invite');

export const setWhoCanInvite = (whoCanInvite: WhoCanInvite) =>
  call<{ whoCanInvite: WhoCanInvite }>('/account/who-can-invite', {
    method: 'PUT',
    body: JSON.stringify({ whoCanInvite }),
  });

/**
 * Is this username free?
 *
 * Advisory only — the server still rejects a collision on save, because between
 * this answer and that save somebody else can take it. Used to tell somebody
 * during the effort of choosing rather than after it.
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  const out = await call<{ available: boolean }>('/is-username-available', {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
  return out.available;
}

/**
 * Upload an avatar.
 *
 * Base64 rather than multipart: the endpoint speaks JSON through Better Auth's
 * body schema, and a second body parser on the authentication service is a
 * second thing to get wrong. These are capped at 1 MB, so the ~33% encoding
 * overhead is a third of a megabyte on a request somebody makes once.
 *
 * The data-URL prefix is stripped HERE rather than server-side, so the endpoint
 * has one input shape to validate instead of two.
 */
export async function uploadAvatar(file: File): Promise<string> {
  const data = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('That file could not be read.'));
    r.onload = () => {
      const result = String(r.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    r.readAsDataURL(file);
  });
  const out = await call<{ image: string }>('/avatar', {
    method: 'POST',
    body: JSON.stringify({ data }),
  });
  return out.image;
}

export const removeAvatar = () => call<{ image: null }>('/avatar', { method: 'DELETE' });

/**
 * The member's own date of birth (RidgeWill/auth#672, over auth#585).
 *
 * `adultChangeUntil` is when an under-18 member may still move themselves to
 * 18 or older (24 hours from sign-up, from a first entry after sign-in, or from
 * their own change from adult to under 18); null when no window is open.
 * `familyGroupRole` set means auth refuses a change to under 18.
 */
export type AgeBand = '13-15' | '16-17' | '18+';

export interface BirthDateInfo {
  dateOfBirth: string | null;
  ageBand: AgeBand | null;
  adultChangeUntil: string | null;
  familyGroupRole: 'starter' | 'guardian' | null;
}

export const getBirthDate = () => call<BirthDateInfo>('/birth-date');

/**
 * Save a date. Refusals arrive as `ApiError` with auth's code:
 * `BIRTH_DATE_NEEDS_SUPPORT`, `BIRTH_DATE_FAMILY_GROUP`, `ACCOUNT_FROZEN`
 * (under 13: every session is revoked) and `BIRTH_DATE_INVALID`.
 */
export const setBirthDate = (dateOfBirth: string) =>
  call<{ ageBand: AgeBand }>('/birth-date', {
    method: 'POST',
    body: JSON.stringify({ dateOfBirth }),
  });

/**
 * Close the signed-in account (RidgeWill/auth#165).
 *
 * ── `confirm` is checked by the SERVER too, and it sends what was TYPED ──
 *
 * The section disables its button until the phrase matches; the service checks
 * the same phrase and refuses a mismatch with `CONFIRMATION_MISMATCH`. This
 * deliberately forwards whatever was typed rather than sending the constant, so
 * the two checks stay independent instead of the server rubber-stamping the
 * client.
 *
 * Worth being precise about what that buys, because it is easy to overclaim: a
 * fixed phrase is on the screen, so it stops a client BUG that fires this call
 * without a confirmation, not a determined forgery. Cross-origin abuse is held
 * off by the origin checks and the SameSite cookie, which is where that
 * property belongs.
 *
 * ── It ends the session, so there is nothing to render afterwards ────
 *
 * `pseudonymizeUser` revokes every grant, so the cookie is dead by the time this
 * resolves. Callers should show a terminal state rather than updating a page
 * that can no longer load anything.
 */
export const closeAccount = (confirm: string) =>
  call<{ closed: boolean; alreadyClosed: boolean; sessionRevoked?: boolean }>('/account/close', {
    method: 'POST',
    body: JSON.stringify({ confirm }),
  });

/**
 * Remove a linked sign-in provider.
 *
 * ── THIS WAS A VERSION-SKEW WORKAROUND, AND IS NOW JUST A CALL ───────
 *
 * It used to bypass `authClient.unlinkAccount()` because the two repositories
 * pinned different minors and this endpoint's contract changed between them:
 * the 1.6 client sent `{ providerId, accountId? }` while the 1.7 server
 * required `{ accountId }`, so the typed helper compiled here and was refused
 * by the deployed service. RidgeWill/account#49 closed that gap by moving the
 * portal onto `^1.7.1`.
 *
 * The old comment said to check the body shape rather than assume it had
 * converged. Checked, on both sides rather than one: `unlink-account` takes
 * `z.object({ accountId: z.string() })` in the client's 1.7.2 and in the
 * service's 1.7.1. So the helper now sends what the server validates, and the
 * hand-rolled call has nothing left to protect.
 *
 * `accountId` is our `account.id`, from `/list-accounts`. Not the provider id,
 * and not the subject id at the provider; all three are in scope on the screen
 * that calls this and only one of them works.
 */
export const unlinkAccount = async (accountId: string) => {
  const { error } = await authClient.unlinkAccount({ accountId });
  // `call()` throws ApiError on a refusal and every caller is written for that.
  // The typed client reports instead of throwing, so the shapes are reconciled
  // here rather than in `sign-in-methods.ts`, which should not have to know
  // which of these two styles a given endpoint happens to use.
  if (error) throw new ApiError(error.status ?? 500, error.message ?? 'Could not remove that sign-in method.');
  return { status: true };
};

/**
 * What this account's email address is currently being moved to, if anything.
 *
 * ── WHY THERE IS AN ENDPOINT FOR THIS AT ALL ─────────────────────────
 *
 * Better Auth keeps no state for a pending change: `/change-email` signs a JWT,
 * mails it, and the change lives entirely inside that link for an hour. So
 * somebody who signed in with their OLD address after a change they had not
 * asked for saw a page that looked completely normal. RidgeWill/auth#327 added
 * `user.pendingEmail` and these two routes; this is the half that reads it.
 *
 * `pendingEmail` is null when there is none, deliberately, so a caller can tell
 * "asked, and there is nothing" from "never asked". The card on the Security
 * page is drawn from a positive answer only and must never be drawn from a
 * failed request.
 *
 * `awaitingApproval` is the SERVER's answer to which address the change is
 * waiting on, added in RidgeWill/auth#334 for exactly this screen. The portal
 * could compute it — the gate is decided by the account's credentials, which
 * are on the same page — and must not: that would be a copy of a rule the auth
 * service owns, evaluated against a different set of facts than the ones the
 * change was recorded against.
 */
export const getPendingEmailChange = () =>
  call<{ pendingEmail: string | null; requestedAt: string | null; awaitingApproval?: boolean }>(
    '/pending-email-change',
  );

/**
 * Take back a pending change.
 *
 * Always 200, including when there was nothing to cancel — the caller is
 * somebody trying to make themselves safe, and refusing them because they were
 * too late, or clicked twice, answers a reasonable act with an error.
 */
export const cancelPendingEmailChange = () =>
  call<{ pendingEmail: null }>('/pending-email-change/cancel', { method: 'POST' });

/**
 * The account's recovery address, and the change waiting on it.
 *
 * ── EVERY FIELD IS THE SERVER'S ANSWER, NOT A DERIVED ONE ────────────
 *
 * `usable` and the two `awaiting` booleans could all be computed here from the
 * timestamps beside them, and must not be, for the reason `awaitingApproval`
 * on the pending email change already records: they are answers about a record
 * this app does not own, and a copy of the rule evaluated against a different
 * set of facts is how a screen comes to disagree with the service it describes.
 *
 * `usable` in particular is a clock comparison, and the clock in question is
 * the server's. A browser with a skewed clock computing it locally would tell
 * somebody their recovery address is ready a day before it is, or refuse to
 * admit it is ready at all.
 *
 * ── THE OPTIONAL FIELDS ARE DEPLOY SKEW, NOT LAZINESS ────────────────
 *
 * This app deploys independently of the auth service and has shipped FIRST
 * before (see `verifyPassword` on `/reauthenticate`). Anything added to the
 * response after the first release is optional here, and the defaults are
 * chosen so an older service produces a coherent screen rather than a wrong
 * one.
 */
export interface RecoveryAddressResponse {
  recoveryEmail: string | null;
  establishedAt: string | null;
  usableAt: string | null;
  usable: boolean;
  pending: {
    recoveryEmail: string;
    requestedAt: string | null;
    awaitingVerification: boolean;
    awaitingApproval: boolean;
  } | null;
  sameDomainAsPrimary?: boolean;
  /**
   * The annual review (RidgeWill/auth#383).
   *
   * `reviewedAt` is null until somebody has confirmed the address at least
   * once, which is the ordinary state for the first year and does NOT mean
   * overdue. `reviewDue` is the question this screen renders on, and it is the
   * SERVER'S answer to the same predicate the reminder job selects on — so the
   * card and the email cannot disagree about whether somebody is being asked
   * for something.
   */
  reviewedAt?: string | null;
  reviewDueAt?: string | null;
  reviewDue?: boolean;
  /**
   * Only on the code-entry POST: whether that code completed the address.
   *
   * Rides alongside the state rather than joining it, for the same reason
   * `confirmationsSent` does — it describes what one call DID, and a `GET` a
   * minute later could not answer it.
   */
  established?: boolean;
  /**
   * Only on a POST, and only about THAT request: whether the two confirmation
   * messages actually went out.
   *
   * The service reports it rather than failing, because `sendEmail` returns
   * false for a policy refusal as readily as a transport failure and every send
   * outside production is refused by design. So a screen that treated a false
   * here as an error would call dev and staging broken.
   */
  confirmationsSent?: boolean;
}

export const getRecoveryAddress = () => call<RecoveryAddressResponse>('/recovery-address');

/**
 * Ask for a recovery address, or replace the one that is there.
 *
 * ── A 200 MEANS TWO MESSAGES WERE SENT, NOT THAT ANYTHING IS SET ─────
 *
 * The same shape as `/change-email`, and the copy has to carry it twice over:
 * this needs BOTH the new address and the current one to confirm before it
 * takes effect, so a 200 is two steps from done rather than one.
 *
 * `currentPassword` is sent when the account has one, exactly as the email
 * change does. The service refuses without it and exempts an account with no
 * credential row, so asking for a password nobody has would be a box that can
 * only be wrong.
 */
export const setRecoveryAddress = (recoveryEmail: string, currentPassword?: string) =>
  call<RecoveryAddressResponse>('/recovery-address', {
    method: 'POST',
    body: JSON.stringify({ recoveryEmail, ...(currentPassword ? { currentPassword } : {}) }),
  });

/**
 * Drop a pending recovery address, which makes BOTH outstanding links inert.
 *
 * Always 200, including when there was nothing to cancel, for the reason
 * `cancelPendingEmailChange` gives.
 */
export const cancelRecoveryAddressChange = () =>
  call<RecoveryAddressResponse>('/recovery-address/cancel', { method: 'POST' });

/**
 * Enter the six-digit code mailed to the proposed recovery address
 * (RidgeWill/auth#393).
 *
 * ── THIS USED TO BE A LINK IN THE EMAIL, AND SHOULD NOT HAVE BEEN ────
 *
 * NIST SP 800-63B specifies the code form directly: "A recovery address SHALL
 * be established only after the subscriber provides the correct confirmation
 * code to the CSP." A clicked link proves somebody holding that mailbox clicked
 * it; a code typed here proves the person SETTING THE ADDRESS UP can read it.
 *
 * Session-gated, which the link could not be — and that is the improvement
 * rather than the cost. The account comes from the session, so the code carries
 * no account id, no destination and no signature, and there is nothing for
 * somebody holding a stolen link to use because there is no link.
 *
 * ── EVERY FAILURE IS ONE FAILURE ─────────────────────────────────────
 *
 * Wrong, expired, spent, never issued: all `RECOVERY_CODE_INVALID`. The service
 * refuses to distinguish them, and this must not invent a distinction from the
 * status code either. The next step is the same in every case, which is to ask
 * for a new one.
 */
export const verifyRecoveryAddressCode = (code: string) =>
  call<RecoveryAddressResponse & { established?: boolean }>('/recovery-address/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });

/**
 * Take the recovery address off the account.
 *
 * Gated like setting one — a fresh session and the current password — but NOT
 * subject to the primary's approval, because removing destroys a route rather
 * than creating one. A removal that had to wait on an approval email would fail
 * the person who has just realised their old address belongs to somebody else,
 * which is the decay this whole feature is built around.
 */
export const removeRecoveryAddress = (currentPassword?: string) =>
  call<RecoveryAddressResponse>('/recovery-address/remove', {
    method: 'POST',
    body: JSON.stringify(currentPassword ? { currentPassword } : {}),
  });

/**
 * Confirm the recovery address is still yours (RidgeWill/auth#383).
 *
 * ── THE LIGHTEST CALL ON THIS SCREEN, DELIBERATELY ───────────────────
 *
 * No password and no fresh session, unlike setting or removing an address.
 * Those create or destroy a route into the account; this one records that the
 * route already there is still the right one, and nothing about the account
 * changes. A password prompt would only lower the completion rate of the one
 * action NIST's annual review depends on.
 *
 * ── AND WHY THE CONFIRMATION IS NOT A LINK IN THE REMINDER EMAIL ─────
 *
 * The reminder cannot name the address: a recovery address is a second copy of
 * the thing being protected and must not sit in a mailbox. So a one-click link
 * in that email would collect a confirmation of something the reader has not
 * been shown — a review that did not happen, resetting the clock for a year on
 * the strength of it. The address is visible on this screen, behind a session,
 * which is the only place the answer can mean anything.
 */
export const confirmRecoveryAddressReview = () =>
  call<RecoveryAddressResponse>('/recovery-address/reviewed', { method: 'POST' });

/**
 * Ask to move the account to another address.
 *
 * ── IT DOES NOT CHANGE THE ADDRESS, AND THE SCREEN HAS TO SAY SO ─────
 *
 * A 200 here means a link has been sent, not that anything moved. The address
 * changes when that link is opened — and, for a password-only account, not even
 * then until the CURRENT address has approved as well.
 *
 * ── `currentPassword` IS OURS, NOT BETTER AUTH'S ─────────────────────
 *
 * `/change-email` takes `{ newEmail, callbackURL? }` upstream and carries only
 * `sensitiveSessionMiddleware`, which asks that a session exists and nothing
 * more. The auth service's `before` hook reads `currentPassword` off the body
 * and refuses without it (RidgeWill/auth#327) — a weaker bar than
 * `/unlink-account`'s would have been backwards, since the address is what
 * every provider sign-in resolves through.
 *
 * So the field is not in the upstream body schema and passes through it: zod
 * strips unknown keys rather than rejecting them, and the hook runs against the
 * raw body. Pinned by auth's `change-email.integration.test.ts`, not by a type.
 *
 * ── NO `callbackURL`, WHICH IS A DECISION ────────────────────────────
 *
 * It defaults to `/` on the hosted login, so confirming lands there rather than
 * back on this portal. Sending somebody back here instead means an ABSOLUTE URL
 * on this origin, and `/verify-email` runs `originCheck` over it at the moment
 * the link is opened: a portal origin missing from the service's
 * `TRUSTED_ORIGINS` would fail the confirmation itself, in an email, hours
 * later, with no way back but asking again. That is not a trade to make on an
 * untested guess. The service already knows `ACCOUNT_WEB_URL` — it uses it in
 * the notice email — so the better fix is a default over there.
 *
 * Sent as one field or two rather than always sending `currentPassword`: an
 * account with no credential row has no password to quote, and the server
 * exempts it rather than refusing forever (that population is the whole reason
 * RidgeWill/auth#324 exists).
 */
export const requestEmailChange = (newEmail: string, currentPassword?: string) =>
  call<{ status: boolean }>('/change-email', {
    method: 'POST',
    body: JSON.stringify({ newEmail, ...(currentPassword ? { currentPassword } : {}) }),
  });

/**
 * Which social providers can actually be connected.
 *
 * Ours, not Better Auth's. Needed so the sign-in section can stop offering
 * "Add provider" once every available one is already connected — a question
 * `/list-accounts` cannot answer, because that says what the ACCOUNT has and
 * nothing about what the SERVICE could offer.
 *
 * A runtime answer rather than a `VITE_` constant, for the reason the hosted
 * login gives about the same endpoint: providers are configured from Railway
 * variables, so a build-time list would freeze at whatever was true when CI
 * ran and enabling one would appear only after somebody rebuilt the frontend.
 *
 * ── ASKED OF THE LOGIN SERVICE, NOT THE ACCOUNT API (account#68) ─────
 *
 * THIS IS THE ONE CALL IN THIS FILE THAT DOES NOT GO TO `BASE`, and reverting
 * it to `call()` puts the "Add provider" button back in the grave.
 *
 * The endpoint answers from the provider credentials present on the deployment
 * serving it. The account deployment has none and is never going to: it refuses
 * sign-in and the OAuth endpoints by design (ADR 0002), so nobody has given it
 * a Google client secret. Asked there, `/providers` truthfully answers `[]`.
 *
 * Which made the portal ask one service about another service's configuration
 * and believe the answer. Measured on dev, 2026-09-02:
 *
 *   account-api-dev  {"providers":[],                            "known":[…]}
 *   auth-dev         {"providers":["google","microsoft","apple"],"known":[…]}
 *
 * `hasProviderToAdd` then reads `[]` as "everything available is already
 * connected" and hides the control — silently, correctly by its own rule, and
 * on a fact gathered from the wrong place. Nothing failed and nothing logged.
 *
 * The right source is the service the button POINTS AT. "What can I connect?"
 * is a question about the login app, `/connect` is where connecting happens,
 * and that deployment is the one holding the credentials that decide the
 * answer.
 *
 * No session, no cookie: the endpoint carries no user data, which is why
 * auth#338 could allow-list it in the first place. `credentials` is left off
 * deliberately rather than by omission — this is the one cross-origin call here
 * that has nothing to authenticate.
 */
export async function getAvailableProviders(): Promise<{
  providers: string[];
  known?: string[];
}> {
  const res = await fetch(`${LOGIN_URL}/api/auth/providers`, {
    headers: { 'content-type': 'application/json' },
  });
  if (!res.ok) throw new ApiError(res.status, `GET /providers failed: ${res.status}`);
  return (await res.json()) as { providers: string[]; known?: string[] };
}

/**
 * Prove the person at the keyboard still knows the account password.
 *
 * ── IT VERIFIES AND CHANGES NOTHING, WHICH IS THE POINT ──────────────
 *
 * The Security page puts its dangerous controls behind a modal that asks for
 * the password FIRST and reveals the field afterwards (Jon, 2026-09-01), and
 * `/change-email` cannot answer "is this password right?" before the person has
 * typed the address they came to type.
 *
 * ── A `{ verified: true }` HERE PERMITS NOTHING ──────────────────────
 *
 * READ THIS BEFORE SIMPLIFYING ANY CALLER. The operation that follows still
 * sends the password and is still checked server-side on its own request:
 * `requestEmailChange` sends `currentPassword`, and `twoFactor.enable` takes
 * one. The tempting next step — "the modal already verified it, so stop
 * sending it" — moves the real check onto a boolean held in a browser.
 *
 * `INVALID_PASSWORD` and `CREDENTIAL_ACCOUNT_NOT_FOUND` are better-auth's own
 * codes, the same ones `/change-password` and `/change-email` give, so a caller
 * that already handles them needs no new branch.
 */
export const reauthenticate = (password: string) =>
  call<{ verified: true }>('/reauthenticate', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });

/**
 * The header Better Auth's captcha plugin reads. Named once, so no screen
 * hand-writes it; the login app spells it the same way in `use-captcha.ts`.
 */
export const CAPTCHA_HEADER = 'x-captcha-response';

const captchaHeaders = (token: string | null): Record<string, string> =>
  token ? { [CAPTCHA_HEADER]: token } : {};

/**
 * Whether the ACCOUNT deployment checks a captcha, and with which site key.
 *
 * ── ASKED OF `BASE`, NOT OF THE LOGIN SERVICE ────────────────────────
 *
 * The opposite choice to `getAvailableProviders`, for the same reason that
 * function gives: ask the deployment that DECIDES. Social providers are decided
 * by the login service, so that call goes there. The captcha on the two
 * email-verification routes is enforced by the deployment that serves them,
 * which is this one, and only its `/providers` can say whether it will demand
 * a token. Asking the login service would render a widget nothing checks, or
 * omit one the server is about to require.
 *
 * `/providers` is already on the account deployment's allow-list (and in
 * contract/auth.contract.test.ts).
 */
export async function getAccountCaptcha(): Promise<CaptchaConfig | null> {
  const res = await fetch(`${BASE}/api/auth/providers`, {
    headers: { 'content-type': 'application/json' },
  });
  if (!res.ok) throw new ApiError(res.status, `GET /providers failed: ${res.status}`);
  const body = (await res.json()) as { captcha?: CaptchaConfig | null };
  return body.captcha ?? null;
}

export interface CaptchaConfig {
  provider: 'cloudflare-turnstile';
  /** Public by design: it is in the widget markup for anyone to read. */
  siteKey: string;
}

/**
 * Send a fresh verification link to the address the account already has
 * (RidgeWill/auth#587).
 *
 * ── THE PERSON TYPES THE ADDRESS, AND THAT IS THE SERVER'S RULE ──────
 *
 * A resend that mailed whatever the session holds would let somebody signed in
 * to an account at an address they do not own keep a stranger's inbox busy. So
 * the service compares what was typed and refuses a mismatch with
 * `EMAIL_MISMATCH`, carrying `correctionAvailable`. This forwards what was
 * typed and never fills it in from the session.
 */
export const resendVerificationLink = (email: string, captchaToken: string | null) =>
  call<{ sent: boolean }>('/email-verification/resend', {
    method: 'POST',
    headers: captchaHeaders(captchaToken),
    body: JSON.stringify({ email }),
  });

/**
 * The ONE immediate correction of a mistyped address, only while unverified
 * (RidgeWill/auth#587).
 *
 * Not `/change-email`, which is refused for an unverified account: that flow
 * asks the CURRENT address to approve, and before verification the current
 * address is usually the typo, a mailbox nobody reads. Both fields are sent and
 * the server compares them too, for the reason `closeAccount` sends what was
 * typed rather than the constant.
 */
export const correctUnverifiedEmail = (
  email: string,
  confirmEmail: string,
  captchaToken: string | null,
) =>
  call<{ email: string; sent: boolean }>('/email-verification/correct-email', {
    method: 'POST',
    headers: captchaHeaders(captchaToken),
    body: JSON.stringify({ email, confirmEmail }),
  });

// ── Family Groups (RidgeWill/account#117, #118; auth#614-#616) ───────

/**
 * Everything the Family Group section needs: my groups and their members, the
 * open invitations (guardians only), my notices, and `mayStart`.
 *
 * `mayStart` is auth's answer to "21 or older, and not a dependent". The
 * portal shows "Start a Family Group" on it alone and never works out an age.
 */
export const getFamily = () => call<MyFamily>('/account/family');

export const startFamilyGroup = () =>
  call<{ groupId: string }>('/account/family/groups', { method: 'POST', body: '{}' });

/**
 * Invite a member (with the two answers: `under18`, and for yes `isGuardian`)
 * or a co-guardian. One email, sent
 * once: inviting an address that already has an open invitation is refused.
 * `emailed` is false where the deployment does not send mail (every test run).
 */
export const inviteToFamily = (groupId: string, input: { kind: FamilyRole; email: string; under18?: boolean; isGuardian?: boolean }) =>
  call<{ invitationId: string; expiresAt: string; emailed: boolean }>(
    `/account/family/groups/${encodeURIComponent(groupId)}/invitations`,
    { method: 'POST', body: JSON.stringify(input) },
  );

export const withdrawFamilyInvitation = (groupId: string, invitationId: string) =>
  call<{ withdrawn: boolean }>(
    `/account/family/groups/${encodeURIComponent(groupId)}/invitations/${encodeURIComponent(invitationId)}/withdraw`,
    { method: 'POST', body: '{}' },
  );

/** Leave. A dependent 18 or older sends `acknowledge: true` unless they already confirmed. */
export const leaveFamilyGroup = (groupId: string, acknowledge?: boolean) =>
  call<{ left: boolean }>(`/account/family/groups/${encodeURIComponent(groupId)}/leave`, {
    method: 'POST',
    body: JSON.stringify(acknowledge ? { acknowledge: true } : {}),
  });

/** The acknowledgment at 18, staying in the group. */
export const acknowledgeFamilyAdult = (groupId: string) =>
  call<{ acknowledgedAt: string }>(`/account/family/groups/${encodeURIComponent(groupId)}/acknowledge`, {
    method: 'POST',
    body: '{}',
  });

export const removeFamilyDependent = (groupId: string, userId: string) =>
  call<{ removed: boolean }>(
    `/account/family/groups/${encodeURIComponent(groupId)}/dependents/${encodeURIComponent(userId)}/remove`,
    { method: 'POST', body: '{}' },
  );

/**
 * The invitation page. POSTs, so the token from the email's link travels in a
 * body rather than in an API path or query string that ends up in a log.
 */
export const viewFamilyInvitation = (token: string) =>
  call<InvitationPreview>('/account/family/invitation', { method: 'POST', body: JSON.stringify({ token }) });

export const acceptFamilyInvitation = (token: string) =>
  call<{ groupId: string; role: FamilyRole }>('/account/family/invitation/accept', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

/** Decline. The inviter is told in the portal; nobody is emailed. */
export const declineFamilyInvitation = (token: string) =>
  call<{ declined: boolean }>('/account/family/invitation/decline', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

export const markFamilyNoticesRead = (ids?: string[]) =>
  call<{ marked: number }>('/account/family/notices/read', {
    method: 'POST',
    body: JSON.stringify(ids ? { ids } : {}),
  });

/** A dependent, as their guardian may see them (auth#616). 404 for anyone else. */
export const getDependent = (userId: string) =>
  call<DependentView>(`/account/family/dependents/${encodeURIComponent(userId)}`);

/** A dependent's agreements with both halves and the full text (auth#615). */
export const getDependentAgreements = (userId: string) =>
  call<{ agreements: DependentAgreement[] }>(`/account/family/dependents/${encodeURIComponent(userId)}/agreements`);

/** Accept for a dependent, and for the guardian in their own name. */
export const acceptForDependent = (userId: string, agreementVersionId: string) =>
  call<{ alreadyAccepted: boolean; dependentPending: string[] }>(
    `/account/family/dependents/${encodeURIComponent(userId)}/agreements/accept`,
    { method: 'POST', body: JSON.stringify({ agreementVersionId }) },
  );
